import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthenticatedResponse } from '../services/api';
import { clearClientCache } from '../features/kpi-consolides/hooks/useCachedFetch';

interface AuthState {
  accessToken: string | null;
  user: AuthenticatedResponse['user'] | null;
  /** Challenge émis après /auth/login, consommé par /auth/mfa/verify. */
  challengeToken: string | null;
  mfaSetupRequired: boolean;
  /**
   * Vrai quand le backend a renvoyé un code `PASSWORD_CHANGE_REQUIRED` sur
   * n'importe quelle route métier. Le router redirige alors vers
   * `/password/change` et bloque l'accès aux autres pages tant que l'utilisateur
   * n'a pas remplacé son mot de passe temporaire. Cf SECURITY.md §1.2.1.
   */
  passwordChangeRequired: boolean;

  setChallenge: (token: string, setupRequired: boolean) => void;
  clearChallenge: () => void;
  setAuthenticated: (payload: AuthenticatedResponse) => void;
  setPasswordChangeRequired: (v: boolean) => void;
  clear: () => void;
}

/**
 * Persistance en localStorage : essentiel pour que les nouveaux onglets
 * (drill-down, Cmd+clic) ne déclenchent pas un /refresh à leur bootstrap.
 *
 * Pourquoi : `rotateRefresh` côté backend détecte le rejeu d'un refresh
 * token (sécurité anti-vol). Si plusieurs onglets bootstrappent en parallèle
 * et appellent /refresh quasi-simultanément avec le MÊME RT cookie (le 1er
 * rotate avant que les autres voient le nouveau cookie), le 2ème déclenche
 * le mismatch et **toutes les sessions sont révoquées**.
 *
 * Avec persistance : un nouvel onglet lit l'access token depuis localStorage
 * → pas de /refresh nécessaire → pas de race possible.
 *
 * Trade-off sécurité : access token en localStorage = lisible par tout JS
 * du même origin. Acceptable car (1) TTL court 15 min, (2) CSP strict, (3)
 * pas de eval/dangerouslySetInnerHTML, (4) le refresh token reste httpOnly.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      challengeToken: null,
      mfaSetupRequired: false,
      passwordChangeRequired: false,

      setChallenge: (token, setupRequired) =>
        set({ challengeToken: token, mfaSetupRequired: setupRequired }),
      clearChallenge: () => set({ challengeToken: null, mfaSetupRequired: false }),
      setAuthenticated: ({ accessToken, user }) =>
        set({
          accessToken,
          user,
          challengeToken: null,
          mfaSetupRequired: false,
        }),
      setPasswordChangeRequired: (v) => set({ passwordChangeRequired: v }),
      clear: () => {
        // Purge des caches RAM client liés à l'auth : évite qu'un user B
        // qui se reconnecte dans le même tab après un user A voie les
        // données KPI mises en cache 20 min par useCachedFetch (cf
        // SECURITY.md §19.5).
        clearClientCache();
        set({
          accessToken: null,
          user: null,
          challengeToken: null,
          mfaSetupRequired: false,
          passwordChangeRequired: false,
        });
      },
    }),
    {
      name: 'crm-auth',
      storage: createJSONStorage(() => localStorage),
      // On ne persiste QUE l'access token + user. Les états transitoires
      // (challengeToken, mfaSetupRequired, passwordChangeRequired) doivent
      // démarrer à zéro à chaque bootstrap.
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
      }),
    },
  ),
);
